<template>
  <div>
    <storm-ui-label
      v-if="label"
      class="flex flex-1"
      :required="rules?.includes('required')"
      :label="label"
    />
    <div class="w-full">
      <div class="relative ">
        <div
          class="flex absolute inset-y-0 right-0 items-center ml-4 cursor-pointer"
          @click="onIconClick"
        >
          <stormy-icon
            name="tabler-calendar"
            class="w-5 h-5 mr-3 !cursor-pointer"
          />
        </div>
        <input
          :id="elementId"
          ref="inputRef"
          v-model="value"
          class="text-base w-full font-normal h-9 placeholder-gray-400 border  border-gray-300 rounded px-2 py-2.5 leading-tight focus:border-blue-400 focus:ring-2 focus:ring-blue-200 focus:outline-none "
          :class="[label ? 'mt-0.5' : '', disabled ? 'bg-gray-50' : '', inputClass, hasError ? 'border-red-300 focus:border-red-400 bg-red-50 focus:ring-red-200' : 'border-gray-300 focus:border-blue-400 focus:ring-blue-200']"
          :disabled="disabled"
          :autocomplete="autocomplete"
          :autofocus="autofocus"
          :name="name"
          :type="type"
          :maxlength="maxlength"
          :readonly="readonly"
          @input="onInput($event.target.value)"
        >
      </div>
      <div class="flex item-center text-sm px-1 py-0.5">
        <div class="flex-1 text-gray-400">
          <slot name="desc" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, onMounted, watch, toRefs, toRef } from 'vue'
import { useField } from 'vee-validate'

import { useProp } from '@/composables/useProp.js'
import Datepicker from 'flowbite-datepicker/Datepicker'
import de from 'flowbite-datepicker/locales/de'
import { useTemplateFilter } from '@/composables/useTemplateFilter.js'
import { id as id2 } from 'random-html-id'

Object.assign(Datepicker.locales, de)
const { formatDate, formatDeDate } = useTemplateFilter()

const inputRef = ref('')
const datePicker = ref()

const emit = defineEmits(['input', 'update:modelValue', 'change'])

const pickDate = (event) => {
  try {
    const pickedDate = event.detail.date.toISOString()
    const formatedDate = formatDate(pickedDate)
    handleChange(formatedDate, false)
    onInput(formatedDate)
  } catch (e) {
    console.log(e)
  }
}

const onInput = (value) => {
  emit('change', value)
}

const props = defineProps({
  label: useProp(String, '', false),
  type: useProp(String, 'text'),
  name: useProp(String),
  inputClass: useProp(String),
  autocomplete: useProp(String),
  minDate: useProp(String, null),
  maxDate: useProp(String, null),
  autofocus: useProp(Boolean),
  readonly: useProp(Boolean),
  disabled: useProp(Boolean),
  checked: useProp(Boolean, false),
  rules: useProp(String),
  datesDisabled: useProp(Array),
  rows: useProp(Number, 5),
  maxlength: useProp(Number, 524288)
})

const { minDate, maxDate } = toRefs(props)

const elementId = computed(() => props.id ?? `date-${id2()}`)

const name = toRef(props, 'name')
const rules = toRef(props, 'rules')
const label = toRef(props, 'label')

const { value, errorMessage, meta, handleChange } = useField(name, rules, { label })
const hasError = computed(() => meta.touched && errorMessage.value !== undefined)

const initDatePicker = (min = null) => {
  if (min === null) min = minDate.value
  const elem = document.querySelector('#' + elementId.value)
  datePicker.value = new Datepicker(elem, {
    orientation: 'bottom right',
    format: 'dd.mm.yyyy',
    weekStart: 1,
    language: 'de',
    minDate: min || null,
    showOnFocus: false,
    showOnClick: false,
    maxDate: maxDate.value,
    datesDisabled: ['12.12.2022', '14.12.2022'],
    autohide: true,
    calendarWeeks: true,
    todayHighlight: true
  })
  inputRef.value.addEventListener('changeDate', pickDate)
}

const onIconClick = () => {
  datePicker.value.show()
}

onMounted(() => {
  initDatePicker()
})

watch(minDate, async (date) => {
  if (datePicker.value) {
    datePicker.value.destroy()
    datePicker.value = null
    initDatePicker(date)
  }
}, { immediate: false })

</script>
